/**
 * Single source of truth for which permission each route requires, consumed
 * by `PermissionRoute` at each route's `element`. Kept as one file instead of
 * a `permission="..."` string improvised per call site (the way
 * `FeatureFlagRoute`'s `feature=` prop is) so "what does this route need" is
 * auditable in one place, and so the exact key string only has to match the
 * rest of the app's usage (e.g. `role.view`) in one location.
 *
 * Every key here is a permission that's already checked somewhere else in
 * the app (a page's own `can(...)` gate, a row action, a header button) -
 * confirmed by grepping actual `can("x.y")`/`canAction("x","y")` call sites
 * before adding an entry, so a route's required permission always matches
 * what the rest of that feature already treats as the real key.
 */
export const ROUTE_PERMISSIONS = {
  roles: { view: "role.view", create: "role.create", edit: "role.edit" },
  userManagement: { view: "user.view" },
  billingUnits: { view: "billingUnit.view" },
  systemFeatures: { view: "feature.view" },
  usageSources: { view: "usageSource.view" },
  usageCompilation: { view: "usageCompilation.view" },
  usageCollection: { view: "usageCollection.view" },
  imports: { view: "import.view", manageFields: "import.edit" },
  migrations: { view: "migration.view", create: "migration.create", edit: "migration.edit" },
  billingLogic: { view: "billingSettings.view", create: "billingSettings.create", edit: "billingSettings.edit" },
  objectCustomization: { view: "objectCustomization.view" },
  webhooks: { view: "webhook.view" },
  integrations: { view: "integration.view" },
  selfServeCheckoutConfig: { edit: "selfServeCheckout.edit" },
  platformSettings: { view: "platformSettings.view" },

  markets: { view: "market.view", edit: "market.edit" },
  plans: { view: "plan.view", create: "plan.create", edit: "plan.edit" },
  products: { view: "product.view", create: "product.create", edit: "product.edit" },

  subscriptions: { view: "subscription.view", create: "subscription.create", edit: "subscription.edit" },
  upsellDownsell: { view: "upsell.view", create: "upsell.create", edit: "upsell.edit" },
  proposals: { view: "proposal.view", create: "proposal.create", edit: "proposal.edit" },
  payments: { view: "payment.view" },
  invoices: { view: "invoice.view", create: "invoice.create" },
  contacts: { view: "contact.view" },
  company: { view: "company.view" },
  selfServeUsers: { view: "selfServeCheckout.view" },

  pricing: { view: "plan.view" },

  // Time Machine (milestone 2) - backed by real, seeded Permission rows
  // (scripts/seeders/permissions/timeMachinePermissions.js), unlike the
  // "deliberately NOT gated" list below - this key IS grantable through the
  // Roles UI, not an invented one.
  timeMachine: { view: "timeMachine.view", create: "timeMachine.create", run: "timeMachine.run" },
};

/**
 * Deliberately NOT gated here: Document management, Exports, Events,
 * Permissions (the admin page listing every permission), and Organisation
 * details. Confirmed against `simplifyserver`/`serverReuseables` - none of
 * these have a real permission module defined anywhere in the backend (no
 * `Permission` catalog seed exists at all, and `routePermissions.js`'s
 * `permission`/`template`/`organisation` entries are dead, commented-out
 * code with zero enforcement). Gating on an invented key like
 * "document.view" would lock every non-wildcard user out of these pages
 * permanently, since there'd be no real permission for an admin to grant
 * back through the Roles UI. Logged in docs/known-issues.md and
 * simplifyserver/docs/known-issues.md instead of worked around here.
 */
